import { Box, Collapse, colors } from '@mui/material';
import { useMemo, useState } from 'react';
import { SectionBox } from './helpers';
import { DependenciesTableMap, DependenciesTable } from './DependenciesTable';
import { ExpandButtonSimple } from '../../common/ExpandButtonSimple';
import { useRunOnce } from '../../common/useRunOnce';
import { DocumentationCardItem } from './types';
import { NoResults } from '../shared/NoResults';
import { ActiveInactiveSelect } from '../building-blocks/activity-state/ActiveInactiveSelect';
import { ActiveState } from '../building-blocks/activity-state/helpers';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { useSearchContext } from '../building-blocks/documentation-card/search/SearchContext';
import {
  PossibleSectionTitleToSectionType,
  BASE_SECTION_EXPANSION,
} from '../building-blocks/documentation-card/search/consts';
import { Typography } from '@sweep-io/sweep-design';
import { useSectionExpansion } from '../building-blocks/documentation-card/search/useSectionExpansion';

interface DependenciesSectionProps {
  metadataId: string;
  configurationItems?: DependenciesTableMap;
  title: string;
  activeState?: ActiveState;
  crmOrgId: string;
  shouldFetchOnMount?: boolean;
  fetchApiOnMount?: ({
    crmOrgId,
    metadataId,
  }: {
    crmOrgId: string;
    metadataId: string;
  }) => Promise<void>;
  onTableItemClick: (props: DocumentationCardItem) => void;
  onToggleActiveInactive?: (value: ActiveState) => void;
}

export const DependenciesSection = ({
  metadataId,
  configurationItems,
  title,
  activeState,
  crmOrgId,
  shouldFetchOnMount,
  fetchApiOnMount,
  onTableItemClick,
  onToggleActiveInactive,
}: DependenciesSectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const { searchTerm, sectionExpansion } = useSearchContext();

  const sectionKey = useMemo(() => {
    return PossibleSectionTitleToSectionType[
      title as keyof typeof PossibleSectionTitleToSectionType
    ];
  }, [title]);

  const { isExpanded, setIsExpanded } = useSectionExpansion({
    searchTerm,
    sectionExpanded: sectionKey ? sectionExpansion[sectionKey] : false,
    defaultExpanded: sectionKey ? BASE_SECTION_EXPANSION[sectionKey] : false,
  });

  useRunOnce(async () => {
    if (shouldFetchOnMount && fetchApiOnMount) {
      setIsLoading(true);
      setIsError(false);
      try {
        await fetchApiOnMount({
          crmOrgId,
          metadataId,
        });

        setIsLoading(false);
      } catch (e) {
        setIsError(true);
        setIsLoading(false);
      }
    }
  });

  const isEmpty = useMemo(
    () =>
      (configurationItems &&
        Object.values(configurationItems).every((item) => item.length === 0)) ||
      !configurationItems,
    [configurationItems],
  );

  return (
    <SectionBox data-section={sectionKey || 'dependencies'}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        sx={{ cursor: 'pointer', width: '100%' }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
          <Typography variant="h5-medium">{title}</Typography>
          {onToggleActiveInactive && activeState && (
            <Box onClick={(event) => event.stopPropagation()}>
              <ActiveInactiveSelect
                withIcon
                selectedItem={activeState}
                onSelect={onToggleActiveInactive}
              />
            </Box>
          )}
        </Box>
        <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
      </Box>
      <Collapse in={isExpanded}>
        <Box pt={2} display="inline-flex" width="100%" flexDirection="column">
          {isLoading && <CenteredCircularProgress size={20} />}
          {!isLoading && isError && (
            <Typography variant="caption" color={colors.grey[700]}>
              Something went wrong, try again later
            </Typography>
          )}
          {isEmpty && !isLoading && <NoResults />}
          {!isLoading && !isEmpty && configurationItems && (
            <DependenciesTable
              onTableItemClick={onTableItemClick}
              configurationItems={configurationItems}
              section={sectionKey || 'dependencies'}
            />
          )}
        </Box>
      </Collapse>
    </SectionBox>
  );
};
